import { useEffect, useRef } from 'react';
import { getSeriesPoints } from '../utils/chartSeriesBus';

// Arrow-key navigation across trace chart data points.
//
// ArrowLeft / ArrowRight  → step ±1 data point
// Shift + ArrowLeft/Right → snap to the nearest multiple-of-10 index in that direction
//
// First key press with no active crosshair jumps to the midpoint of visible data.
// Position is clamped at the first/last point; no wrap-around.

export function useChartKeyboardNav() {
  const currentDistFtRef = useRef(null);

  // Track crosshair position set by any chart or the map
  useEffect(() => {
    const handler = (e) => {
      currentDistFtRef.current = e.detail?.distFt ?? null;
    };
    window.addEventListener('trackapp:chart-hover', handler);
    window.addEventListener('trackapp:map-hover',   handler);
    return () => {
      window.removeEventListener('trackapp:chart-hover', handler);
      window.removeEventListener('trackapp:map-hover',   handler);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      // Don't steal keys from text inputs
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      const pts = getSeriesPoints();
      if (!pts.length) return;

      e.preventDefault();

      const direction = e.key === 'ArrowRight' ? 1 : -1;
      const cur = currentDistFtRef.current;

      let idx;

      if (cur == null) {
        // No active crosshair — jump to midpoint
        idx = Math.floor(pts.length / 2);
      } else {
        // Find the index of the point nearest to the current distFt
        let best = 0;
        let bestDiff = Math.abs(pts[best].distFt - cur);
        for (let i = 1; i < pts.length; i++) {
          const diff = Math.abs(pts[i].distFt - cur);
          if (diff < bestDiff) { best = i; bestDiff = diff; }
        }

        if (e.shiftKey) {
          // Snap to the nearest multiple-of-10 index in the direction of travel
          if (direction > 0) {
            idx = Math.floor(best / 10) * 10 + 10;
          } else {
            idx = Math.ceil(best / 10) * 10 - 10;
          }
        } else {
          idx = best + direction;
        }
      }

      // Clamp to valid range
      idx = Math.max(0, Math.min(pts.length - 1, idx));

      const pt = pts[idx];
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
        detail: {
          distFt:      pt.distFt,
          lat:         pt.lat       ?? null,
          lon:         pt.lon       ?? null,
          videoTime:   pt.videoTime ?? null,
          fromKeyboard: true,
        },
      }));
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}
